import React, { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  Button as MuiButton,
  DialogTitle,
  Dialog,
  DialogContent,
  FormControl,
  Popover,
  Stack,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { getCommercialRegistrationMaxBulkSearchCount } from "@/configs/commercialRegistration";
import { Divider } from "@/components/Divider";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle, SectionTitle } from "@/components/Title";
import { FeedGrid } from "./components/FeedGrid";
import { CommercialImportStatus } from "./components/CommercialImportStatus";
import {
  type ICommercialRegistrationReceptionBookFeedPresenterProps,
  type UserSearchHistoryCommercialRegistrationReceptionBookResponseType,
} from "./types";
import { SearchCondition } from "./components/SearchCondition";
import { SizingWrapper, SizingWrapperStyle } from "@/components/Wrapper";
import { AcquireBookFormUI } from "@/components/AcquireBook/AcquireBookFormUI";
import { type ISingleBookAcquireDialogData } from "../../types/singleBookAcquireDialog";
import { SearchHistoryModal } from "./components/SearchHistoryModal";
import type {
  FetchParams,
  ReceptionBookBulkGridSelection,
} from "@/features/commercialBook/types";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useCommercialMaskUnlockPrice } from "./hooks/useCommercialMaskUnlockPrice";

const PricingHelpButton: React.FC = () => {
  const { unitPrice } = useCommercialMaskUnlockPrice();
  const [pricingAnchorEl, setPricingAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const pricingHelpOpen = Boolean(pricingAnchorEl);
  const pricingHelpId = pricingHelpOpen
    ? "commercial-pricing-help-popover"
    : undefined;

  return (
    <>
      <MuiButton
        aria-describedby={pricingHelpId}
        variant="outlined"
        size="small"
        startIcon={<HelpOutlineIcon fontSize="small" />}
        onClick={(e) => {
          setPricingAnchorEl(e.currentTarget);
        }}
      >
        課金について
      </MuiButton>
      <Popover
        id={pricingHelpId}
        open={pricingHelpOpen}
        anchorEl={pricingAnchorEl}
        onClose={() => {
          setPricingAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
            会社法人等番号 / 法人名 はセットで{" "}
            {unitPrice !== null
              ? `¥${unitPrice.toLocaleString("ja-JP")}`
              : "取得中..."}
            /件。
          </Typography>
          <Typography variant="body2">
            ・セルの「閲覧」ボタンで個別課金
          </Typography>
          <Typography variant="body2">
            ・行を複数選択して一括課金も可
          </Typography>
          <Typography variant="body2">
            ・一度閲覧したレコードは再課金不要
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

const CommercialRegistrationReceptionBookFeedPresenter: React.FC<
  ICommercialRegistrationReceptionBookFeedPresenterProps
> = (props) => {
  const { gridApiRef, handleSearch, commercialImportStatuses, feedParams } =
    props;
  const maxBulkSearch = getCommercialRegistrationMaxBulkSearchCount();
  const isReadOnly = useReadOnlyUser();

  // 登記/図面取得ダイアログ表示/非表示を決定するフラグ
  const [singleBookAcquireDialogStatus, setSingleBookAcquireDialogStatus] =
    useState<boolean>(false);

  // 登記/図面取得ダイアログで使用するデータをまとめて格納するState
  const [singleBookAcquireDialogData, setSingleBookAcquireDialogData] =
    useState<ISingleBookAcquireDialogData>({
      prefecturesId: 0,
      prefectures: "",
      address: "",
      chiban: "",
      kaokuNumber: "",
      realEstateTypeEng: "",
    });

  // DataGridに行の選択状態をクリアする指示を出すState
  // setClearSelection(true)で選択状態クリア
  const [clearSelection, setClearSelection] = React.useState(false);

  // 選択行の会社法人等番号（ユニーク化済）と、グリッド表示用の法人名（API非送信）
  const [receptionBookBulkSelection, setReceptionBookBulkSelection] =
    useState<ReceptionBookBulkGridSelection>({
      corporateNumbers: [],
      corporateNumberDisplayNames: {},
    });
  const selectedCount = receptionBookBulkSelection.corporateNumbers.length;
  const isOverLimit = selectedCount > maxBulkSearch;

  const navigate = useNavigate();

  const handleAcquireFromCorporateName = (): void => {
    if (selectedCount === 0 || isOverLimit) {
      return;
    }
    const state: FetchParams = {
      matchMethod: "会社法人等番号",
      matchType: "完全一致",
      closed: false,
      kubun: "商業及び法人",
      prefectureName: "全国",
      location: "なし",
      corporateNumbers: receptionBookBulkSelection.corporateNumbers,
      corporateNumberDisplayNames:
        receptionBookBulkSelection.corporateNumberDisplayNames,
    };
    navigate("/commercialbook/companylist", { state });
  };

  // 再検索が行われたら、DataGridの選択状態をクリアする
  // 初回マウント時（feedParams === null）はクリア指示を出さない
  useEffect(() => {
    if (feedParams !== null) {
      setClearSelection(true);
    }
  }, [feedParams]);

  // 検索履歴モーダルの状態管理
  const [searchHistoryModalOpen, setSearchHistoryModalOpen] = useState(false);

  // 検索履歴モーダルを開く
  const handleOpenSearchHistory = (): void => {
    setSearchHistoryModalOpen(true);
  };

  // 検索履歴モーダルを閉じる
  const handleCloseSearchHistory = (): void => {
    setSearchHistoryModalOpen(false);
  };

  // 検索履歴が選択された時の処理（商業登記用）
  const handleSelectSearchHistory = (
    _data: UserSearchHistoryCommercialRegistrationReceptionBookResponseType
  ): void => {
    // TODO: 商業登記用検索履歴APIが実装された際に、検索条件の復元処理を実装する
    // 現在はAPIが存在しないため、空の実装
    // 将来的に以下のフィールドを復元予定:
    // - companyNumber: 会社法人等番号
    // - companyName: 法人名
    // - companyType: 種別
    // - receptionDateStart/receptionDateEnd: 受付年月日
    // - receptionNumber: 受付番号
    // - receptionType: 登記の種類
    // - remarks: 備考
  };

  const handleSingleBookAcquireDialogClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    setSingleBookAcquireDialogStatus(false);
  };

  return (
    <>
      {/* 登記1件取得のダイアログ */}
      <Dialog
        open={singleBookAcquireDialogStatus}
        onClose={handleSingleBookAcquireDialogClose}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"登記および図面選択"}
        </DialogTitle>
        <DialogContent>
          <AcquireBookFormUI
            mode={"modal"}
            singleBookAcquireDialogData={singleBookAcquireDialogData}
            isReadOnly={isReadOnly}
            handleClose={() => {
              setSingleBookAcquireDialogStatus(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <PagePaper>
        <Accordion defaultExpanded sx={{ mb: 1 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography variant="h6">お知らせ</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CommercialImportStatus importStatuses={commercialImportStatuses} />
          </AccordionDetails>
        </Accordion>

        <Box sx={{ display: "flex", mb: 3 }}>
          <MenuBookIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>商業・法人異動情報から検索する</PageTitle>
        </Box>

        {/* 検索条件 */}
        <SearchCondition
          handleSearch={handleSearch}
          handleOpenSearchHistory={handleOpenSearchHistory}
          startDate={props.startDate}
          minDate={props.minDate}
          maxDate={props.maxDate}
          requesterTypeOptions={props.requesterTypeOptions}
          commercialReceptionReasonOptions={
            props.commercialReceptionReasonOptions
          }
          prefectureOptions={props.prefectureOptions}
          errorMessages={{
            dateStart: props.errorMessages.dateStart,
            dateEnd: props.errorMessages.dateEnd,
          }}
          plan={props.plan}
        />

        <Box sx={{ mt: 3 }} />

        {/* 商業登記受付帳一覧テーブル */}
        <SectionPaper>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems="center"
          >
            <SectionTitle>商業・法人異動情報一覧</SectionTitle>
            <Stack
              mx={2}
              my={2}
              direction="row"
              spacing={1}
              alignItems="center"
            >
              {props.plan === "pay_per_view" && <PricingHelpButton />}
              <SizingWrapper size={SizingWrapperStyle.INHERIT}>
                <FormControl fullWidth>
                  <MuiButton
                    variant="outlined"
                    onClick={handleAcquireFromCorporateName}
                    disabled={selectedCount === 0 || isOverLimit}
                    sx={{ textAlign: "center" }}
                  >
                    選択した法人の
                    <br />
                    商業登記簿を取得する
                  </MuiButton>
                  {isOverLimit && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      選択は{maxBulkSearch}件以下にしてください
                    </Typography>
                  )}
                </FormControl>
              </SizingWrapper>
            </Stack>
          </Stack>

          <Divider />

          <FeedGrid
            apiRef={gridApiRef}
            clearSelection={clearSelection}
            setClearSelection={setClearSelection}
            feedParams={feedParams}
            plan={props.plan}
            onReceptionBookBulkSelectionChange={setReceptionBookBulkSelection}
          />

          <Divider />

          <Box sx={{ mb: 6 }} />
        </SectionPaper>
      </PagePaper>

      {/* 検索履歴モーダル */}
      <SearchHistoryModal
        open={searchHistoryModalOpen}
        onClose={handleCloseSearchHistory}
        onSelect={handleSelectSearchHistory}
      />
    </>
  );
};

export { CommercialRegistrationReceptionBookFeedPresenter };
